import { useMemo } from "react";
import useSWR from "swr";
import type { Address } from "viem";

import { fetchPnlHistory } from "lib/indexer/client";
import { bigNumberify } from "lib/numbers";
import { EMPTY_ARRAY } from "lib/objects";

export type PnlSummaryPoint = {
  bucketLabel: string;
  losses: number;
  pnlBps: bigint;
  pnlUsd: bigint;
  realizedPnlUsd: bigint;
  unrealizedPnlUsd: bigint;
  startUnrealizedPnlUsd: bigint;
  volume: bigint;
  wins: number;
  winsLossesRatioBps: bigint | undefined;
  usedCapitalUsd: bigint;
} & PnlSummaryPointDebugFields;

export type PnlSummaryPointDebugFields = {
  realizedBasePnlUsd: bigint;
  realizedFeesUsd: bigint;
  realizedPriceImpactUsd: bigint;
  unrealizedBasePnlUsd: bigint;
  unrealizedFeesUsd: bigint;
  startUnrealizedBasePnlUsd: bigint;
  startUnrealizedFeesUsd: bigint;
};

export const DEBUG_QUERY = null;

export function usePnlSummaryData(chainId: number, account: Address) {
  const { data, error, isLoading } = useSWR(
    account ? ["usePnlSummaryData", chainId, account] : null,
    async () => {
      const res = await fetchPnlHistory(account, { buckets: 30, period: "day" });
      return res.buckets.map((b: any): PnlSummaryPoint => {
        const pnlUsd = bigNumberify(b.pnlUsd ?? "0") ?? 0n;
        return {
          bucketLabel: new Date(b.bucketTs * 1000).toLocaleDateString(),
          losses: b.losses ?? 0,
          pnlBps: 0n,
          pnlUsd,
          realizedPnlUsd: pnlUsd,
          unrealizedPnlUsd: 0n,
          startUnrealizedPnlUsd: 0n,
          volume: bigNumberify(b.volumeUsd ?? "0") ?? 0n,
          wins: b.wins ?? 0,
          winsLossesRatioBps: undefined,
          usedCapitalUsd: 0n,
          realizedBasePnlUsd: pnlUsd,
          realizedFeesUsd: 0n,
          realizedPriceImpactUsd: 0n,
          unrealizedBasePnlUsd: 0n,
          unrealizedFeesUsd: 0n,
          startUnrealizedBasePnlUsd: 0n,
          startUnrealizedFeesUsd: 0n,
        };
      });
    }
  );

  return useMemo(
    () => ({ data: data ?? (EMPTY_ARRAY as PnlSummaryPoint[]), error, loading: isLoading }),
    [data, error, isLoading]
  );
}
